/**
 * Composant pour la configuration Stripe API Key
 * Permet aux vendeurs d'utiliser leur propre compte Stripe
 * Inclut une configuration AUTOMATIQUE du webhook
 * 
 * Design aligné sur la charte graphique CashLive
 */

import React, { useState, useEffect } from 'react';
import { toast } from 'react-hot-toast';
import { Eye, EyeOff, AlertCircle, CheckCircle, Key, Shield, Loader2, Webhook, ExternalLink, TestTube, Info } from 'lucide-react';

import { StripeKeyValidationService, type StripeKeyValidationResult } from '@/services/stripe/stripe-key-validation-service';
import StripeKeyValidationStatus from '@/components/stripe/StripeKeyValidationStatus';
import { StripeWebhookManagementService } from '@/services/stripe/stripe-webhook-management-service';
import { StripeSecretsService } from '@/services/stripe/stripe-secrets-service';
import { Card, CardHeader, CardBody, Button, TailwindDialog, DialogContent, DialogActions } from '@/components/ui';
import PaypalExtensionConfig from './PaypalExtensionConfig';

// Import des images du tutoriel
import stripeStep1Image from '@/assets/stripe-interface-api-key.png';
import stripeStep2Image from '@/assets/tripe-interface-api-key-step-2.png';
import stripeStep3Image from '@/assets/stripe-interface-api-key-step-3 .png';

interface StripeApiKeyConfigProps {
    organizationId: string;
    stripeSecretKey?: string;
    stripePublishableKey?: string;
    stripeApiKeyWebhookSecret?: string;
    paypalEnabled?: boolean;
    lastWebhookReceivedAt?: string;
    onConfigChange: (config: {
        stripeSecretKey?: string;
        stripePublishableKey?: string;
        stripeApiKeyWebhookSecret?: string;
        paypalEnabled?: boolean;
    }) => void;
}

// Étapes de configuration avec progression
type ConfigStep =
    | 'idle'
    | 'validating_keys'
    | 'configuring_webhook'
    | 'testing_reception'
    | 'success'
    | 'error';

const StripeApiKeyConfig: React.FC<StripeApiKeyConfigProps> = ({
    organizationId,
    stripeSecretKey,
    stripePublishableKey,
    stripeApiKeyWebhookSecret,
    paypalEnabled = false,
    lastWebhookReceivedAt,
    onConfigChange,
}) => {
    const [isLoading, setIsLoading] = useState(false);
    const [isTesting, setIsTesting] = useState(false);
    const [isEditMode, setIsEditMode] = useState(false); // Mode édition pour les clés déjà configurées
    const [currentStep, setCurrentStep] = useState<ConfigStep>('idle');
    const [validationResult, setValidationResult] = useState<StripeKeyValidationResult | null>(null);
    const [showSecretKey, setShowSecretKey] = useState(false);
    const [localSecretKey, setLocalSecretKey] = useState(stripeSecretKey || '');
    const [localPublishableKey, setLocalPublishableKey] = useState(stripePublishableKey || '');
    const [localPaypalEnabled, setLocalPaypalEnabled] = useState(paypalEnabled);
    const [currentTutorialStep, setCurrentTutorialStep] = useState(0);
    const [testResults, setTestResults] = useState<{
        keysValid?: boolean;
        webhookReception?: boolean;
    } | null>(null);
    const [showResultModal, setShowResultModal] = useState(false);
    const [modalMode, setModalMode] = useState<'testing_keys' | 'testing_webhook' | 'saving' | 'result'>('testing_keys');
    const [showHelpModal, setShowHelpModal] = useState(false);
    const [isUpdatingWebhook, setIsUpdatingWebhook] = useState(false);

    // Synchroniser les props avec le state local quand les props changent (ex: après sauvegarde)
    useEffect(() => {
        if (stripeSecretKey !== undefined) setLocalSecretKey(stripeSecretKey || '');
        if (stripePublishableKey !== undefined) setLocalPublishableKey(stripePublishableKey || '');
        if (paypalEnabled !== undefined) setLocalPaypalEnabled(paypalEnabled);
    }, [stripeSecretKey, stripePublishableKey, paypalEnabled]);

    const isConfigured = !!stripeSecretKey && (stripeSecretKey.length > 0 || stripeSecretKey.includes('...'));
    const isShowingMasked = localSecretKey.includes('...');
    const hasWebhookConfigured = !!stripeApiKeyWebhookSecret;

    // Validation stricte des clés Stripe
    // La clé secrète (restricted key) doit commencer par rk_ ou sk_ et contenir _test_ ou _live_
    const isValidSecretKeyFormat = (localSecretKey.startsWith('rk_') || localSecretKey.startsWith('sk_')) &&
        (localSecretKey.includes('_test_') || localSecretKey.includes('_live_'));
    const isValidSecretKey = isShowingMasked || isValidSecretKeyFormat;

    // La clé publique doit obligatoirement commencer par pk_ et contenir _test_ ou _live_
    const isValidPublishableKeyFormat = localPublishableKey.startsWith('pk_') &&
        (localPublishableKey.includes('_test_') || localPublishableKey.includes('_live_'));
    const isValidPublishableKey = !localPublishableKey || isValidPublishableKeyFormat;

    // Vérifier si une clé pk a été mise dans le champ secret key (erreur commune)
    const isPublishableKeyInSecretField = localSecretKey.startsWith('pk_');

    const isTestMode = localSecretKey.includes('_test_');
    const isRestrictedKey = localSecretKey.startsWith('rk_');

    // Images du tutoriel
    const tutorialSteps = [
        {
            title: "Accédez aux Clés API",
            description: "Allez dans Développeurs → Clés API et cliquez sur \"Créer une clé limitée\"",
            image: stripeStep1Image
        },
        {
            title: "Choisissez le type de clé",
            description: "Sélectionnez \"Fournir cette clé à un autre site Web\" et cliquez sur Continuer",
            image: stripeStep2Image
        },
        {
            title: "Nommez votre clé",
            description: "Entrez \"CashLive\" comme nom et cochez \"Personnaliser les autorisations liées à cette clé\"",
            image: stripeStep3Image
        }
    ];

    // Réinitialiser la validation quand les clés changent
    const handleSecretKeyChange = (value: string) => {
        setLocalSecretKey(value);
        setValidationResult(null);
        setCurrentStep('idle');
        setTestResults(null);
    };

    const handlePublishableKeyChange = (value: string) => {
        setLocalPublishableKey(value);
        setValidationResult(null);
        setTestResults(null);
    };

    // BATTERIE DE TESTS COMPLÈTE
    const handleRunTests = async () => {
        // Vérifier si une clé pk a été mise dans le champ secret key
        if (isPublishableKeyInSecretField) {
            toast.error('Erreur : Vous avez mis une clé publique (pk_) dans le champ de clé secrète. Veuillez utiliser une clé restreinte (rk_) ou secrète (sk_).');
            return;
        }

        if (!localSecretKey || !isValidSecretKey) {
            toast.error('La clé secrète doit commencer par rk_ ou sk_');
            return;
        }

        setIsTesting(true);
        setTestResults(null);
        setValidationResult(null);
        setModalMode('testing_keys');
        setShowResultModal(true);

        try {
            // TEST 1: Validation des clés API (permissions, format, etc.)
            let validationRes;

            // Si la clé est masquée, elle est déjà dans Secret Manager -> On teste via le backend
            if (localSecretKey.includes('...')) {
                validationRes = await StripeKeyValidationService.validateSavedKeys(organizationId);
            } else {
                // Nouvelle clé saisie -> Validation classique via POST (avant sauvegarde)
                validationRes = await StripeKeyValidationService.validateKeys({
                    secretKey: localSecretKey,
                    publishableKey: localPublishableKey || undefined,
                });
            }

            setValidationResult(validationRes);
            setTestResults({ keysValid: validationRes.isValid, webhookReception: undefined });
            setModalMode('result');

            if (!validationRes.isValid) {
                toast.error(validationRes.errors[0] || '❌ Clés API invalides - Vérifiez les détails');
            } else {
                toast.success('✅ Clés API valides !');
            }

        } catch (error: any) {
            console.error('Test error:', error);
            toast.error('Erreur lors des tests');
            setModalMode('result');
        } finally {
            setIsTesting(false);
        }
    };

    const handleRunWebhookTest = async () => {
        if (!hasWebhookConfigured) {
            toast.error('Le webhook doit être configuré avant de pouvoir le tester');
            return;
        }

        setIsTesting(true);
        setTestResults({ keysValid: true, webhookReception: false }); // Reset state
        setValidationResult(null);
        setModalMode('testing_webhook');
        setShowResultModal(true);

        try {
            const testResult = await StripeWebhookManagementService.testWebhook(organizationId);

            if (testResult.success) {
                const startTime = Date.now();
                let received = false;

                for (let attempt = 0; attempt < 8; attempt++) {
                    await new Promise(resolve => setTimeout(resolve, 2000));

                    try {
                        const status = await StripeWebhookManagementService.checkWebhookTestStatus(organizationId);
                        if (status.received && status.lastReceivedAt) {
                            const receivedTime = new Date(status.lastReceivedAt).getTime();
                            if (receivedTime > startTime - 10000) {
                                received = true;
                                break;
                            }
                        }
                    } catch { /* continue polling */ }
                }

                setTestResults({ keysValid: true, webhookReception: received });
                if (received) {
                    toast.success('✅ Webhook Stripe reçu avec succès !');
                } else {
                    toast.error('⚠️ Webhook non reçu (Stripe peut être lent)');
                }
            } else {
                toast.error('❌ Échec de l\'envoi de l\'événement de test');
            }
            setModalMode('result');
        } catch (error: any) {
            console.error('Webhook test error:', error);
            toast.error('Erreur lors du test du webhook');
            setModalMode('result');
        } finally {
            setIsTesting(false);
        }
    };

    const handleRecreateWebhook = async () => {
        if (!isConfigured) {
            toast.error('Configurez d’abord les clés Stripe');
            return;
        }

        const confirmed = window.confirm(
            'Cette action va supprimer l’ancien webhook Stripe et en créer un nouveau. Continuer ?'
        );
        if (!confirmed) return;

        setIsUpdatingWebhook(true);
        try {
            const result = await StripeWebhookManagementService.recreateWebhook(organizationId);

            if (!result.success) {
                throw new Error(result.error || 'Échec de la mise à jour du webhook');
            }

            toast.success('Webhook Stripe recréé avec succès');

            if (result.webhookSecretSaved) {
                // Mettre à jour l'affichage parent sans toucher aux clés
                const metadata = await StripeSecretsService.getMetadata(organizationId);
                if (metadata) {
                    onConfigChange({
                        stripeSecretKey: localSecretKey,
                        stripePublishableKey: localPublishableKey,
                        stripeApiKeyWebhookSecret: metadata.webhookSecretMasked || undefined,
                        paypalEnabled: localPaypalEnabled,
                    });
                }
            }
        } catch (error: any) {
            console.error('Recreate webhook error:', error);
            toast.error(error.message || 'Erreur lors de la recréation du webhook');
        } finally {
            setIsUpdatingWebhook(false);
        }
    };


    // Fonction principale: Enregistrer & Configurer
    const handleSaveAndConfigure = async () => {
        // Vérifier si une clé pk a été mise dans le champ secret key
        if (isPublishableKeyInSecretField) {
            toast.error('Erreur : Vous avez mis une clé publique (pk_) dans le champ de clé secrète. Veuillez utiliser une clé restreinte (rk_) ou secrète (sk_).');
            return;
        }

        if (!localSecretKey || !isValidSecretKey) {
            toast.error('La clé secrète Stripe doit commencer par rk_ ou sk_');
            return;
        }

        if (!localPublishableKey) {
            toast.error('La clé publique Stripe est requise');
            return;
        }

        if (!isValidPublishableKey) {
            toast.error('La clé publique doit commencer par pk_ et contenir _test_ ou _live_');
            return;
        }

        setIsLoading(true);
        setValidationResult(null);
        setModalMode('saving');
        setShowResultModal(true);
        const toastId = 'config-progress';

        try {
            // ÉTAPE 1: Validation des clés
            setCurrentStep('validating_keys');
            toast.loading('Validation des clés...', { id: toastId });

            let validationRes;
            if (isShowingMasked) {
                validationRes = await StripeKeyValidationService.validateSavedKeys(organizationId);
            } else {
                validationRes = await StripeKeyValidationService.validateKeys({
                    secretKey: localSecretKey,
                    publishableKey: localPublishableKey || undefined,
                });
            }

            setValidationResult(validationRes);

            if (!validationRes.isValid) {
                setCurrentStep('error');
                toast.error(validationRes.errors[0] || 'Clés invalides', { id: toastId });
                setModalMode('result');
                setIsLoading(false);
                return;
            }

            // ÉTAPE 2: Sauvegarde sécurisée et Configuration Webhook
            setCurrentStep('configuring_webhook');
            toast.loading('Sauvegarde sécurisée et configuration...', { id: toastId });

            // Utiliser le nouveau service sécurisé
            let saveResult;
            try {
                saveResult = await StripeSecretsService.saveSecrets({
                    organizationId,
                    secretKey: localSecretKey,
                    publishableKey: localPublishableKey || '',
                    paypalEnabled: localPaypalEnabled,
                });

                if (!saveResult.success) {
                    throw new Error(saveResult.error || 'Échec de la sauvegarde sécurisée');
                }

                console.log('✅ Secrets saved securely:', {
                    maskedSecretKey: saveResult.maskedSecretKey,
                    webhookConfigured: saveResult.webhookConfigured,
                });

            } catch (saveError: any) {
                console.error('Save secrets error:', saveError);
                setCurrentStep('error');
                toast.error(saveError.message || 'Échec sauvegarde sécurisée', { id: toastId });
                setModalMode('result');
                setIsLoading(false);
                return;
            }

            // ÉTAPE 3: Test de réception (optionnel)
            setCurrentStep('testing_reception');
            toast.loading('Test de réception...', { id: toastId });

            try {
                await StripeWebhookManagementService.testWebhook(organizationId);
            } catch { /* Non bloquant */ }

            // SUCCÈS
            setCurrentStep('success');
            setModalMode('result');
            toast.success('Configuration terminée !', { id: toastId });

            // Notifier le parent
            onConfigChange({
                stripeSecretKey: saveResult.maskedSecretKey || StripeSecretsService.maskKey(localSecretKey),
                stripePublishableKey: saveResult.maskedPublishableKey || localPublishableKey,
                stripeApiKeyWebhookSecret: saveResult.maskedWebhookSecret,
                paypalEnabled: localPaypalEnabled,
            });

            setIsEditMode(false);

        } catch (error: any) {
            console.error('Configuration error:', error);
            setCurrentStep('error');
            setModalMode('result');
            toast.error(error.message || 'Erreur de configuration', { id: toastId });
        } finally {
            setIsLoading(false);
        }
    };


    // Labels dynamiques
    const getButtonLabel = () => {
        switch (currentStep) {
            case 'validating_keys': return 'Validation...';
            case 'configuring_webhook': return 'Config. webhook...';
            case 'testing_reception': return 'Test réception...';
            default: return 'Enregistrer & Configurer';
        }
    };

    return (
        <div className="space-y-6">
            {/* Header Section - Style charte graphique regroupé dans une Card */}
            <Card variant="default">
                <CardHeader>
                    <div className="flex items-center gap-3">
                        <Key className="w-5 h-5 text-primary" />
                        <h2 className="text-lg font-semibold text-foreground">Connecter mon compte Stripe</h2>
                    </div>
                </CardHeader>
                <CardBody className="p-5">
                    <p className="text-sm text-foreground-secondary">
                        L'argent des ventes va directement sur votre compte Stripe.
                        <strong> Commissions facturées mensuellement</strong>,
                        <strong> frais de port à gérer manuellement</strong>.
                        Virement Stripe sous 2-7 jours selon votre compte.
                    </p>
                    {isConfigured && (
                        <div className="flex items-center gap-2 mt-4">
                            <span className="flex items-center gap-1.5 px-2.5 py-1 bg-success/10 text-success text-xs font-medium rounded-full">
                                <CheckCircle className="w-3.5 h-3.5 flex-shrink-0" />
                                <span className="leading-none">Configuré</span>
                            </span>
                            {hasWebhookConfigured && (
                                <span className="flex items-center gap-1.5 px-2.5 py-1 bg-info/10 text-info text-xs font-medium rounded-full">
                                    <Webhook className="w-3.5 h-3.5 flex-shrink-0" />
                                    <span className="leading-none">Webhook actif</span>
                                </span>
                            )}
                        </div>
                    )}
                </CardBody>
            </Card>




            {/* Formulaire de configuration regroupé dans une Card */}
            <Card variant="default">
                <CardBody className="p-5 space-y-5">
                    {/* Champs Input - Uniquement si non configuré OU en mode édition */}
                    {(!isConfigured || isEditMode) ? (
                        <>
                            {isEditMode && (
                                <div className="flex items-center justify-between mb-4 pb-2 border-b border-border/50">
                                    <h3 className="text-sm font-medium text-foreground">Modification des clés</h3>
                                    <Button
                                        variant="ghost"
                                        size="sm"
                                        onClick={() => {
                                            setIsEditMode(false);
                                            setLocalSecretKey(stripeSecretKey || '');
                                            setLocalPublishableKey(stripePublishableKey || '');
                                        }}
                                        className="text-foreground-secondary h-8"
                                    >
                                        Annuler
                                    </Button>
                                </div>
                            )}

                            {/* Clé secrète */}
                            <div>
                                <div className="flex justify-between items-center mb-2">
                                    <label className="text-sm font-medium text-foreground">
                                        Clé Restreinte Stripe
                                    </label>
                                    <button
                                        onClick={() => setShowHelpModal(true)}
                                        className="text-xs text-primary hover:text-primary-dark transition-colors flex items-center gap-1"
                                    >
                                        <Info className="w-3.5 h-3.5 flex-shrink-0" />
                                        <span className="leading-none">Comment créer ma clé ?</span>
                                    </button>
                                </div>
                                <div className="relative">
                                    <input
                                        type={showSecretKey ? 'text' : 'password'}
                                        value={localSecretKey}
                                        onChange={(e) => handleSecretKeyChange(e.target.value)}
                                        placeholder="Clé restreinte Stripe"
                                        className="w-full px-4 py-3 pr-12 rounded-xl border border-border bg-background text-foreground placeholder:text-foreground-tertiary focus:border-primary focus:ring-2 focus:ring-primary/20 transition-all"
                                    />
                                    <button
                                        type="button"
                                        onClick={() => setShowSecretKey(!showSecretKey)}
                                        className="absolute right-3 top-1/2 -translate-y-1/2 text-foreground-secondary hover:text-foreground p-1"
                                    >
                                        {showSecretKey ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                                    </button>
                                </div>
                                <div className="flex flex-wrap gap-2 mt-2">
                                    {isRestrictedKey && (
                                        <span className="flex items-center gap-1 text-xs text-success">
                                            <CheckCircle className="w-3.5 h-3.5" />
                                            Clé restreinte ✓
                                        </span>
                                    )}
                                    {isTestMode && (
                                        <span className="flex items-center gap-1 text-xs text-warning">
                                            <AlertCircle className="w-3.5 h-3.5" />
                                            Mode test
                                        </span>
                                    )}
                                    {localSecretKey && !isShowingMasked && isPublishableKeyInSecretField && (
                                        <span className="flex items-center gap-1 text-xs text-error">
                                            <AlertCircle className="w-3.5 h-3.5" />
                                            Erreur : Vous avez mis une clé publique (pk_). Ce champ nécessite une clé restreinte (rk_) ou secrète (sk_).
                                        </span>
                                    )}
                                    {localSecretKey && !isShowingMasked && !isValidSecretKey && !isPublishableKeyInSecretField && (
                                        <span className="flex items-center gap-1 text-xs text-error">
                                            <AlertCircle className="w-3.5 h-3.5" />
                                            Format invalide - La clé doit commencer par rk_ ou sk_
                                        </span>
                                    )}
                                </div>
                            </div>

                            {/* Clé publique */}
                            <div>
                                <label className="block text-sm font-medium text-foreground mb-2">
                                    Clé publique Stripe
                                </label>
                                <input
                                    type="text"
                                    value={localPublishableKey}
                                    onChange={(e) => handlePublishableKeyChange(e.target.value)}
                                    placeholder="Clé publique Stripe"
                                    className="w-full px-4 py-3 rounded-xl border border-border bg-background text-foreground placeholder:text-foreground-tertiary focus:border-primary focus:ring-2 focus:ring-primary/20 transition-all"
                                />
                                {/* Messages d'erreur pour la clé publique */}
                                {localPublishableKey && !isValidPublishableKey && (
                                    <div className="flex items-center gap-1 mt-2 text-xs text-error">
                                        <AlertCircle className="w-3.5 h-3.5" />
                                        <span>
                                            {localPublishableKey.startsWith('rk_') || localPublishableKey.startsWith('sk_')
                                                ? 'Erreur : Vous avez mis une clé secrète. Ce champ nécessite une clé publique (pk_).'
                                                : 'Format invalide - La clé publique doit commencer par pk_'
                                            }
                                        </span>
                                    </div>
                                )}
                                {localPublishableKey && isValidPublishableKey && (
                                    <div className="flex items-center gap-1 mt-2 text-xs text-success">
                                        <CheckCircle className="w-3.5 h-3.5" />
                                        <span>Clé publique valide ✓</span>
                                    </div>
                                )}
                            </div>
                        </>
                    ) : (
                        /* Affichage résumé quand configuré */
                        <div className="space-y-4">
                            <div className="flex items-center justify-between">
                                <h3 className="text-sm font-medium text-foreground-secondary">Clés actives</h3>
                                <Button
                                    variant="outline"
                                    size="sm"
                                    onClick={() => setIsEditMode(true)}
                                    leftIcon={<Key className="w-4 h-4" />}
                                    className="h-8 py-0"
                                >
                                    Modifier les clés
                                </Button>
                            </div>
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                <div className="p-3 bg-background-tertiary rounded-xl border border-border/50">
                                    <p className="text-[10px] uppercase tracking-wider font-bold text-foreground-tertiary mb-1">Clé Secrète</p>
                                    <p className="text-sm font-mono text-foreground truncate">{localSecretKey}</p>
                                </div>
                                <div className="p-3 bg-background-tertiary rounded-xl border border-border/50">
                                    <p className="text-[10px] uppercase tracking-wider font-bold text-foreground-tertiary mb-1">Clé Publique</p>
                                    <p className="text-sm font-mono text-foreground truncate">{localPublishableKey}</p>
                                </div>
                            </div>
                        </div>
                    )}

                    {/* Note sécurité */}
                    <div className="flex items-start gap-3 p-3 bg-success/5 rounded-lg border border-success/10">
                        <Shield className="w-5 h-5 text-success flex-shrink-0" />
                        <div className="text-xs">
                            <p className="font-medium text-success">Connexion sécurisée</p>
                            <p className="text-foreground-secondary mt-0.5">
                                Vos clés sont stockées de manière sécurisée. Nous n'avons jamais accès à vos fonds.
                            </p>
                        </div>
                    </div>

                    {/* Status Webhook - Uniquement si configuré (Invisible tant que non configuré) */}
                    {hasWebhookConfigured && (
                        <div className="flex items-center gap-3 py-2 px-1">
                            <div className="w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 bg-success/10 text-success">
                                <CheckCircle className="w-4 h-4" />
                            </div>
                            <div className="flex-1">
                                <p className="text-sm font-medium text-foreground">
                                    Synchronisation active
                                </p>
                                <p className="text-xs text-foreground-secondary">
                                    {hasWebhookConfigured ? (
                                        <>
                                            Le webhook est configuré et opérationnel.
                                            {lastWebhookReceivedAt && (
                                                <span className="block mt-1 font-medium text-primary">
                                                    Dernière activité : {new Date(lastWebhookReceivedAt).toLocaleString('fr-FR')}
                                                </span>
                                            )}
                                        </>
                                    ) : 'Webhook non détecté'}
                                </p>
                            </div>
                        </div>
                    )}

                    {/* Options PayPal Extension */}
                    <PaypalExtensionConfig
                        organizationId={organizationId}
                        id="stripe_api_key_details"
                        paypalEnabled={localPaypalEnabled}
                        onChange={(config) => {
                            setLocalPaypalEnabled(config.paypalEnabled);

                            // Si on est déjà configuré, on veut peut-être sauvegarder immédiatement
                            if (isConfigured && !isEditMode) {
                                onConfigChange({
                                    stripeSecretKey: localSecretKey,
                                    stripePublishableKey: localPublishableKey,
                                    paypalEnabled: config.paypalEnabled,
                                });
                            }
                        }}
                    />

                    {/* Barre de progression Enregistrer & Configurer */}
                    {isLoading && (
                        <div className="p-4 rounded-xl border border-border bg-background-tertiary">
                            <div className="flex items-center gap-3 mb-3">
                                <Loader2 className="w-5 h-5 animate-spin text-primary" />
                                <span className="font-medium text-foreground">{getButtonLabel()}</span>
                            </div>
                            <div className="flex gap-1">
                                <div className={`flex-1 h-1.5 rounded-full transition-all ${['validating_keys', 'configuring_webhook', 'testing_reception', 'success'].includes(currentStep)
                                    ? 'bg-primary' : 'bg-background-secondary'
                                    }`} />
                                <div className={`flex-1 h-1.5 rounded-full transition-all ${['configuring_webhook', 'testing_reception', 'success'].includes(currentStep)
                                    ? 'bg-primary' : 'bg-background-secondary'
                                    }`} />
                                <div className={`flex-1 h-1.5 rounded-full transition-all ${['testing_reception', 'success'].includes(currentStep)
                                    ? 'bg-primary' : 'bg-background-secondary'
                                    }`} />
                            </div>
                        </div>
                    )}

                    {/* Boutons d'action - Tester à gauche, Enregistrer à droite */}
                    <div className="flex flex-col sm:flex-row gap-3">
                        <Button
                            variant="outline"
                            onClick={handleRunTests}
                            disabled={isTesting || isLoading || !localSecretKey || (!isShowingMasked && !isValidSecretKey)}
                            isLoading={isTesting && modalMode === 'testing_keys' && !testResults?.webhookReception}
                            leftIcon={(!isTesting || modalMode !== 'testing_keys') && <TestTube className="w-5 h-5" />}
                            className="flex-1"
                        >
                            Tester les clés
                        </Button>

                        {isConfigured && !isEditMode && (
                            <Button
                                variant="outline"
                                onClick={handleRunWebhookTest}
                                disabled={isTesting || isLoading || isUpdatingWebhook || !hasWebhookConfigured}
                                isLoading={isTesting && testResults?.webhookReception === false}
                                leftIcon={!isTesting && <Webhook className="w-5 h-5" />}
                                className="flex-1 border-info/50 text-info hover:bg-info/5"
                            >
                                Tester le Webhook
                            </Button>
                        )}

                        {isConfigured && !isEditMode && (
                            <Button
                                variant="outline"
                                onClick={handleRecreateWebhook}
                                disabled={isTesting || isLoading || isUpdatingWebhook}
                                isLoading={isUpdatingWebhook}
                                leftIcon={!isUpdatingWebhook && <Webhook className="w-5 h-5" />}
                                className="flex-1 border-warning/50 text-warning hover:bg-warning/5"
                            >
                                Mettre à jour le Webhook
                            </Button>
                        )}

                        {/* Bouton Enregistrer & Configurer - Uniquement si mode édition OU non configuré */}
                        {(!isConfigured || isEditMode) && (
                            <Button
                                variant="primary"
                                onClick={handleSaveAndConfigure}
                                disabled={isLoading || isTesting || !localSecretKey || !isValidSecretKey}
                                isLoading={isLoading}
                                leftIcon={!isLoading && <Shield className="w-5 h-5" />}
                                className="flex-1"
                            >
                                {isEditMode ? 'Enregistrer les modifications' : getButtonLabel()}
                            </Button>
                        )}
                    </div>
                </CardBody>
            </Card>


            {/* MODALE DE RÉSULTATS */}
            <TailwindDialog
                isOpen={showResultModal}
                onClose={() => setShowResultModal(false)}
                title={
                    modalMode === 'testing_keys' ? 'Validation des clés...' :
                        modalMode === 'testing_webhook' ? 'Test du webhook...' :
                            modalMode === 'saving' ? 'Configuration en cours...' :
                                (validationResult?.isValid && currentStep === 'success') ? '✅ Configuration réussie' :
                                    (validationResult?.isValid || (testResults?.webhookReception && !validationResult)) ? '✅ Test réussi' :
                                        (testResults?.webhookReception === false && !validationResult) ? '⚠️ Problème de réception' :
                                            '❌ Erreurs détectées'
                }
                maxWidth="2xl"
                variant="themed"
                showCloseButton={modalMode === 'result'}
            >
                <div className="overflow-y-auto max-h-[70vh]">
                    <DialogContent className={modalMode === 'result' ? 'py-0 px-6' : 'p-6 space-y-4'}>
                        {/* Progression pendant les tests ou l'enregistrement */}
                        {(modalMode === 'testing_keys' || modalMode === 'testing_webhook' || modalMode === 'saving') && (
                            <div className="space-y-6">
                                <div className="flex items-center justify-center">
                                    <div className="w-16 h-16 rounded-full bg-primary/20 flex items-center justify-center">
                                        <Loader2 className="w-8 h-8 text-primary animate-spin" />
                                    </div>
                                </div>

                                <div className="text-center">
                                    <p className="text-lg font-medium text-foreground">
                                        {modalMode === 'testing_keys' && 'Validation des clés et permissions...'}
                                        {modalMode === 'testing_webhook' && 'Envoi d\'un événement de test...'}
                                        {modalMode === 'saving' && currentStep === 'validating_keys' && 'Validation des clés...'}
                                        {modalMode === 'saving' && currentStep === 'configuring_webhook' && 'Configuration du webhook...'}
                                        {modalMode === 'saving' && currentStep === 'testing_reception' && 'Test de réception...'}
                                    </p>
                                    <p className="text-sm text-foreground-secondary mt-2">
                                        Veuillez patienter...
                                    </p>
                                </div>

                                <div className="space-y-2">
                                    <div className="flex gap-2">
                                        <div className="flex-1 h-2 rounded-full bg-primary animate-pulse" />
                                        <div className={`flex-1 h-2 rounded-full transition-all ${(modalMode === 'saving' && ['configuring_webhook', 'testing_reception', 'success'].includes(currentStep))
                                            ? 'bg-primary animate-pulse'
                                            : 'bg-background-tertiary'
                                            }`} style={{ animationDelay: '0.2s' }} />
                                        <div className={`flex-1 h-2 rounded-full transition-all ${(modalMode === 'saving' && ['testing_reception', 'success'].includes(currentStep))
                                            ? 'bg-primary animate-pulse'
                                            : 'bg-background-tertiary'
                                            }`} style={{ animationDelay: '0.4s' }} />
                                    </div>
                                    {modalMode === 'saving' && (
                                        <div className="flex justify-between text-[10px] text-foreground-tertiary">
                                            <span>Validation</span>
                                            <span>Webhook</span>
                                            <span>Test</span>
                                        </div>
                                    )}
                                </div>
                            </div>
                        )}

                        {/* Résultats */}
                        {modalMode === 'result' && (
                            <div className="space-y-4">
                                {/* Message de succès pour Enregistrer & Configurer */}
                                {/* Message de succès affiché via le composant StripeKeyValidationStatus */}

                                {/* Résultats de validation détaillés (ou Webhook seul) */}
                                {(validationResult || (testResults?.webhookReception !== undefined)) && (
                                    <StripeKeyValidationStatus
                                        isLoading={false}
                                        result={validationResult || ((testResults?.webhookReception !== undefined) ? null : {
                                            isValid: true,
                                            secretKeyValid: true,
                                            publishableKeyValid: true,
                                            webhookSecretValid: true,
                                            permissionsValid: true,
                                            isTestMode: (localSecretKey || '').includes('_test_'),
                                            errors: [],
                                            warnings: [],
                                            permissions: { detected: [], dangerous: [], missing: [] },
                                            tests: {
                                                secretKey: { passed: true, message: 'Déjà validé' },
                                                publishableKey: { passed: true, message: 'Déjà validé' },
                                                webhookSecret: { passed: true, message: 'Déjà validé' },
                                                permissions: { passed: true, message: 'Déjà validées', details: [] },
                                                paymentIntent: { passed: true, message: 'Déjà validé' },
                                                applePay: { passed: true, message: 'Déjà validé' },
                                            }
                                        } as any)}
                                        onRetry={() => {
                                            setShowResultModal(false);
                                            handleRunTests();
                                        }}
                                        webhookTestResult={testResults?.webhookReception !== undefined ? {
                                            tested: true,
                                            received: !!testResults?.webhookReception
                                        } : undefined}
                                    />
                                )}
                            </div>
                        )}
                    </DialogContent>

                    {/* Actions */}
                    {modalMode === 'result' && (
                        <DialogActions className="flex justify-end py-4 px-6 border-t border-border bg-background-secondary">
                            <Button
                                onClick={() => setShowResultModal(false)}
                                variant="primary"
                                className="px-8"
                            >
                                Fermer
                            </Button>
                        </DialogActions>
                    )}
                </div>
            </TailwindDialog>

            {/* MODALE D'AIDE / TUTORIEL */}
            <TailwindDialog
                isOpen={showHelpModal}
                onClose={() => setShowHelpModal(false)}
                title="Comment créer votre clé sécurisée"
                maxWidth="2xl"
                variant="themed"
                showCloseButton={true}
            >
                <div className="overflow-y-auto max-h-[80vh]">
                    <DialogContent className="p-6 space-y-6">
                        {/* Navigation */}
                        <div className="flex items-center justify-center gap-2 mb-4">
                            {tutorialSteps.map((_, index) => (
                                <button
                                    key={index}
                                    onClick={() => setCurrentTutorialStep(index)}
                                    className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold transition-all ${currentTutorialStep === index
                                        ? 'bg-primary text-white'
                                        : 'bg-background-tertiary text-foreground-secondary hover:bg-primary/10'
                                        }`}
                                >
                                    {index + 1}
                                </button>
                            ))}
                        </div>

                        {/* Contenu */}
                        <div className="text-center space-y-2">
                            <h4 className="text-lg font-medium text-foreground">
                                Étape {currentTutorialStep + 1}: {tutorialSteps[currentTutorialStep].title}
                            </h4>
                            <p className="text-sm text-foreground-secondary">
                                {tutorialSteps[currentTutorialStep].description}
                            </p>
                        </div>

                        {/* Image */}
                        <div className="rounded-xl overflow-hidden border border-border/50 shadow-lg bg-background-tertiary">
                            <img
                                src={tutorialSteps[currentTutorialStep].image}
                                alt={`Étape ${currentTutorialStep + 1}`}
                                className="w-full h-auto object-contain max-h-[400px]"
                            />
                        </div>

                        {/* Permissions (visible tout le temps pour référence rapide) */}
                        <div className="bg-warning/5 border border-warning/10 rounded-xl p-4">
                            <p className="text-sm font-semibold text-warning mb-2 flex items-center gap-2">
                                <AlertCircle className="w-4 h-4" />
                                Permissions requises (en "Écriture") :
                            </p>
                            <ul className="text-xs text-foreground-secondary space-y-1.5 pl-6 list-disc">
                                <li><code className="bg-background-tertiary px-1.5 py-0.5 rounded text-foreground font-mono">PaymentIntents</code></li>
                                <li><code className="bg-background-tertiary px-1.5 py-0.5 rounded text-foreground font-mono">Webhook Endpoints</code></li>
                            </ul>
                        </div>
                    </DialogContent>

                    <DialogActions className="flex justify-between items-center p-6 border-t border-border bg-background-secondary sticky bottom-0 z-10">
                        {/* Navigation Gauche */}
                        <Button
                            variant="outline"
                            onClick={() => setCurrentTutorialStep(Math.max(0, currentTutorialStep - 1))}
                            disabled={currentTutorialStep === 0}
                        >
                            ← Précédent
                        </Button>

                        {/* Action Centrale : Lien Stripe */}
                        <Button
                            variant="primary"
                            leftIcon={<ExternalLink className="w-4 h-4" />}
                            onClick={() => window.open("https://dashboard.stripe.com/apikeys/create?type=restricted_key", "_blank")}
                        >
                            Créer ma clé sur Stripe
                        </Button>

                        {/* Navigation Droite */}
                        <Button
                            variant="outline"
                            onClick={() => {
                                if (currentTutorialStep < tutorialSteps.length - 1) {
                                    setCurrentTutorialStep(Math.min(tutorialSteps.length - 1, currentTutorialStep + 1));
                                } else {
                                    setShowHelpModal(false);
                                }
                            }}
                        >
                            {currentTutorialStep === tutorialSteps.length - 1 ? 'Terminer' : 'Suivant →'}
                        </Button>
                    </DialogActions>
                </div>
            </TailwindDialog>
        </div>
    );
};

export default StripeApiKeyConfig;
